import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import { flushSync } from 'react-dom';

import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';

import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import { exportStuckUsers, fixStuckUsers } from 'api/WorkflowsAPI';
import { FIX_STUCK_USERS_MODAL } from 'modules/workflows/constants';
import { closeModal, getWorkflow } from 'modules/workflows/ducks';
import {
  StyledRadioList,
  HiddenCSVLink,
  LabelText,
  RadioFieldWrapper,
  FieldWrapper,
  StyledInputText,
} from './styles';
import {
  HOW_TO_PROCEED_OPTIONS,
  HOW_DEFAULT_OPTION,
  WHEN_TO_PROCEED_OPTIONS,
  WHEN_DEFAULT_OPTION,
  DATE_TIME,
} from './constants';

type StuckUsersModalProps = {
  /** Whether modal is open and visible */
  isOpen: boolean;
};

export const FIX_STUCK_USERS_BUTTON_ID = 'fix-stuck-users-button';
export const DAYS_INPUT_TEXT = 'safe-delete-days';
export const DRY_RUN_CHECKBOX = 'dry-run';

export const FixStuckUsersModal = ({ isOpen }: StuckUsersModalProps) => {
  const dispatch = useDispatch();
  const csvLink = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const workflow = useSelector(getWorkflow);

  const [howToProceedOption, setHowToProceedOption] = useState(HOW_DEFAULT_OPTION);
  const [whenToProceedOption, setWhenToProceedOption] = useState(WHEN_DEFAULT_OPTION);
  const [safeDaysValue, setSafeDaysValue] = useState('365');
  const [isDryRun, setIsDryRun] = useState(false);
  const [csvData, setCsvData] = useState('');

  const handleCloseModal = () => dispatch(closeModal(FIX_STUCK_USERS_MODAL));

  const handleExportStucksUsers = async () => {
    try {
      const res = await exportStuckUsers(workflow?.id, Number(safeDaysValue));
      flushSync(() => {
        setCsvData(res);
      });
      // Callback to mimick the button click for CSVLink
      csvLink?.current?.link?.click();
      dispatch(addPositiveToast('Stuck users successfully exported'));
    } catch {
      dispatch(addNegativeToastWithErrorId('Sorry, please try again later.', 'exportStucksUsers'));
    }
  };

  const handleFixStuckUsers = async () => {
    const payload = {
      workflowId: workflow?.id,
      safeDeleteDays: Number(safeDaysValue),
      dryRun: isDryRun,
      clearUsers: howToProceedOption === HOW_DEFAULT_OPTION,
      onlyProceedIfSafe: whenToProceedOption === WHEN_DEFAULT_OPTION,
    };

    try {
      const res = await fixStuckUsers(payload);
      dispatch(addPositiveToast(res, { noDelay: true }));
      handleCloseModal();
    } catch {
      dispatch(
        addNegativeToastWithErrorId(
          'Sorry, unable to fix stuck users. Please try again later.',
          'fixStuckUsers',
        ),
      );
    }
  };

  return (
    <Modal
      id={FIX_STUCK_USERS_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>Fix stuck workflow users</ModalHeader>
      <ModalBody>
        <RadioFieldWrapper>
          <p>Would you like to clear users, or have them proceed?</p>
          <StyledRadioList
            name="how-to-proceed-list"
            options={HOW_TO_PROCEED_OPTIONS}
            onSelect={setHowToProceedOption}
            value={howToProceedOption}
          />
        </RadioFieldWrapper>
        <RadioFieldWrapper>
          <p>Would you like users to proceed only if it’s safe?</p>
          <StyledRadioList
            name="how-to-proceed-list"
            options={WHEN_TO_PROCEED_OPTIONS}
            onSelect={setWhenToProceedOption}
            value={whenToProceedOption}
          />
        </RadioFieldWrapper>
        <FieldWrapper>
          <StyledInputText
            id="safe-delete-days"
            data-test={DAYS_INPUT_TEXT}
            value={`${safeDaysValue}`}
            onChange={setSafeDaysValue}
            type="number"
            hasError={!safeDaysValue}
          />
          <LabelText>Safe delete days</LabelText>
        </FieldWrapper>
        <FieldWrapper>
          <Checkbox
            data-test={DRY_RUN_CHECKBOX}
            status={isDryRun ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
            onClick={() => setIsDryRun(!isDryRun)}
          />
          <LabelText>Dry run mode</LabelText>
        </FieldWrapper>
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={FIX_STUCK_USERS_BUTTON_ID}
          onClick={handleFixStuckUsers}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
          isDisabled={!safeDaysValue}
        >
          OK
        </Button>
        <Button
          onClick={handleExportStucksUsers}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          Export stuck users
        </Button>
        <HiddenCSVLink
          data={csvData}
          filename={`stuck_workflows_${workflow?.id}_${DATE_TIME}.csv`}
          ref={csvLink}
          target="_blank"
        />
      </ModalFooter>
    </Modal>
  );
};
